import { MakeRouteMatchUnion } from "@tanstack/react-router";
import {
  DomainGetDomains200DataItem,
  DomainGetDomains200DataItemMetadata,
} from "../gen/model";
import baseCss from "../styles.css?inline";
// function asJavascriptScript(s) {
//   return {
//     id: "wrapper-for-" + s.id,
//     children: `
//       (() => {
//         const el = document.createElement("script");
//         el.id = "${s.id}";
//         el.src = "${s.src}";
//         el.async = true;
//         el.defer = true;
//         el.type = "module";
//         document.currentScript.after(el);
//       })();
//     `,
//   };
// }

// <link rel="stylesheet" href="/path/to/my.css" media="print" onload="this.media='all'; this.onload=null;">
function linkViaScript(url: string) {
  return {
    children: `
      (() => {
        const el = document.createElement("link");
        el.rel = "stylesheet";
        el.media = "print";
        el.href = "${url}";
        el.onload = () => { el.media = 'all'; };
        document.currentScript.after(el);
      })();
    `,
  };
}

let posthogInitialized = false;

class HeadManager {
  public async getCss(domainName: string) {
    const domainNameWithUnderscores = domainName.replaceAll(/\./g, "_");
    const customCss = await import(
      `../styles-${domainNameWithUnderscores}.css?inline`
    ).catch((e) => {
      console.error("Cannot find custom style", domainNameWithUnderscores);
      return `/** ${domainNameWithUnderscores} not found**/`;
    });
    return baseCss + "\n/* Custom CSS */\n" + customCss.default;
  }

  public setTitle(t: string) {
    if (typeof window !== "undefined") {
      window.document.title = t;
    }
  }

  public initializePostHog(domain: DomainGetDomains200DataItem, posthog: any) {
    if (!domain?.services?.posthog) {
      return;
    }
    const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
    const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;
    if (!posthogInitialized && posthogKey && posthogHost) {
      posthog.init(posthogKey, {
        api_host: posthogHost,
        defaults: "2026-01-30",
        disable_session_recording: false,
        persistence: "localStorage",
        capture_pageview: "history_change",
        disable_surveys: true,
      });
      posthogInitialized = true;
    }
  }

  public getMetaTags(
    md: DomainGetDomains200DataItemMetadata | undefined,
    currentUrl?: string,
    logoUrl?: string,
  ) {
    md = md || {};

    // Helper function to ensure absolute URL (WhatsApp requires absolute URLs)
    const ensureAbsoluteUrl = (url: string | undefined): string | undefined => {
      if (!url) return undefined;
      // If already absolute (starts with http:// or https://), return as is
      if (/^https?:\/\//.test(url)) return url;
      // If relative, we'd need a base URL - for now return as is
      // In production, you might want to construct absolute URL using domain
      return url;
    };

    const ogImageUrl = ensureAbsoluteUrl(
      md.og_image?.formats?.medium?.url || md.og_image?.url,
    );

    return [
      { charSet: "UTF-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      md.description && { name: "description", content: md.description },
      // Open Graph tags (used by Facebook, WhatsApp, LinkedIn, etc.)
      md.og_title && { property: "og:title", content: md.og_title },
      md.og_description && {
        property: "og:description",
        content: md.og_description,
      },
      ogImageUrl && { property: "og:image", content: ogImageUrl },
      logoUrl && { property: "og:logo", content: logoUrl },
      md.og_type && { property: "og:type", content: md.og_type },
      // og:url is important for WhatsApp to identify the canonical URL
      currentUrl && { property: "og:url", content: currentUrl },
      // Twitter Card tags
      md.twitter_title && {
        property: "twitter:title",
        content: md.twitter_title,
      },
      md.twitter_description && {
        property: "twitter:description",
        content: md.twitter_description,
      },
      md.twitter_image?.url && {
        property: "twitter:image",
        content: md.twitter_image?.url,
      },
      md.twitter_card && { name: "twitter:card", content: md.twitter_card },
    ].filter((x) => x);
  }

  public getHotReloadSupportScriptTags() {
    if (!import.meta.env.PROD) {
      return [
        {
          type: "module",
          src: "/@vite/client",
        },
        {
          type: "module",
          children: `import RefreshRuntime from "/@react-refresh"
  RefreshRuntime.injectIntoGlobalHook(window)
  window.$RefreshReg$ = () => {}
  window.$RefreshSig$ = () => (type) => type
  window.__vite_plugin_react_preamble_installed__ = true`,
        },
      ];
    }
    return [];
  }



  public getMetaPixel(domain: DomainGetDomains200DataItem) {
    if (domain?.services?.metapixel) {
      return [
        {
          type: "module",
          children: `
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${domain?.services?.metapixel}');
fbq('track', 'PageView');
      `,
        },
      ];
    }
    return [];
  }
  public getOpenAIPixel(domain: DomainGetDomains200DataItem) {
    if (domain?.services?.openai) {
      return [
        {
          type: "module",
          children: `
      !function(w, d, s, u) {
        if (w.oaiq) return;
        var q = function() {
          q.q.push(arguments);
        };
        q.q = [];
        w.oaiq = q;
        var j = d.createElement(s);
        j.async = 1;
        j.src = u;
        var f = d.getElementsByTagName(s)[0];
        f.parentNode.insertBefore(j, f);
      }(window, document, "script", "https://bzrcdn.openai.com/sdk/oaiq.min.js");
      oaiq("consent", true);
      oaiq("init", {
        pixelId: "${domain?.services?.openai}",
        debug: true
      });          
          `
        }
      ]
    }
    return [];
  }

  public getMicrosoftAdsPixel(domain: DomainGetDomains200DataItem) {
    if (domain?.services?.microsoft_ads) {
      return [
        {
          type: "module",
          children: `
            (function(w, d, t, u, o) {
              w[u] = w[u] || [], o.ts = (new Date).getTime();
              var n = d.createElement(t);
              n.src = "https://bat.bing.net/bat.js?ti=" + o.ti + ("uetq" != u ? "&q=" + u : "");
              n.async = 1;
              n.onload = n.onreadystatechange = function() {
                var s = this.readyState;
                s && "loaded" !== s && "complete" !== s || (
                  o.q = w[u], w[u] = new UET(o), w[u].push("pageLoad"), n.onload = n.onreadystatechange = null
                )
              };
              var i = d.getElementsByTagName(t)[0];
              i.parentNode.insertBefore(n, i);
            })(window, document, "script", "uetq", {
              ti: "${domain?.services?.microsoft_ads}",
              enableAutoSpaTracking: true
            });
          `,
        },
      ];
    }
    return [];
  }

  public getGoogleTagPixel(domain: DomainGetDomains200DataItem) {
    if (domain?.services?.gtag) {
      return [
        {
          async: true,
          defer: true,
          type: "module",
          src: `https://www.googletagmanager.com/gtag/js?id=${domain?.services.gtag}`,
        },
        {
          children: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${domain?.services?.gtag}');
          `,
        },
      ];
    }

    return [];
  }

  public getHeadScripts(domain: DomainGetDomains200DataItem) {
    if (domain?.services?.booking_engine) {
      return [linkViaScript("https://static.mirai.com/core/index.css")];
    }

    return [];
  }

  public getLinksTags(domain: DomainGetDomains200DataItem) {
    return [{ rel: "icon", href: "/images/favicon.png" }].filter(Boolean);
  }

  public async routerHead(ctx: any): Promise<{
    links: any[];
    meta: any[];
    scripts: any[];
    styles: any[];
  }> {
    const { clientScript, domain, domainName } = ctx.match.context;
    const metadata = this.getMetadata([ctx.match], domain?.metadata);

    // Get current URL for og:url tag (important for WhatsApp)
    // In SSR, we can get it from ctx.location, in client from window.location
    const currentUrl = `https://${domainName}${ctx.match.pathname}`;

    const metaTags = this.getMetaTags(
      metadata,
      currentUrl,
      domain?.logo_small?.url,
    );
    const hotReloadSupport = this.getHotReloadSupportScriptTags();
    const metaPixel = this.getMetaPixel(domain);
    const openAiPixel = this.getOpenAIPixel(domain);
    const gtagPixel = this.getGoogleTagPixel(domain);
    const microsoftAdsPixel = this.getMicrosoftAdsPixel(domain);
    const headScripts = this.getHeadScripts(domain);
    const linksTags = this.getLinksTags(domain);
    if (ctx.match.id !== "__root__/") {
      linksTags.push({ rel: "canonical", href: currentUrl });
    }

    return {
      links: linksTags,
      meta: metaTags,
      scripts: [
        ...hotReloadSupport,
        ...metaPixel,
        ...openAiPixel,
        ...gtagPixel,
        ...microsoftAdsPixel,
        ...headScripts,
        {
          type: "module",
          preconnect: "true",
          src: clientScript,
        },
      ],
      styles: [],
    };
  }

  public getTitle(matches: MakeRouteMatchUnion[]) {
    return matches
      .map((m) => m.context.title)
      .filter((t) => t)
      .join(" | ");
  }

  getMetadata(
    matches: MakeRouteMatchUnion[],
    defaultMetadata?: DomainGetDomains200DataItemMetadata,
  ): DomainGetDomains200DataItemMetadata | undefined {
    return matches
      .map((m) => m.context.metadata)
      .reduce((acc, obj) => {
        return { ...acc, ...obj };
      }, defaultMetadata || {});
  }
}

export default new HeadManager();
